import React, { useRef, useEffect, useState } from 'react';
import api from '../services/api';
import { BACKEND_URL } from '../config';

const TYPE_LABELS = {
  depot: 'DÉPÔT',
  retrait: 'RETRAIT',
  transfert: 'TRANSFERT',
  facture: 'PAIEMENT FACTURE',
  credit: 'ACHAT CRÉDIT',
};

const formatMoney = (v) => {
  const num = parseFloat(v) || 0;
  return num.toLocaleString('fr-FR', { minimumFractionDigits: 0 }) + ' F CFA';
};

const MobileMoneyReceipt = ({ transaction, onClose }) => {
  const receiptRef = useRef();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get('/business/settings')
      .then(res => setSettings(res.data))
      .catch(() => {});
  }, []);

  const handlePrint = () => {
    const content = receiptRef.current;
    const originalContents = document.body.innerHTML;
    document.body.innerHTML = content.innerHTML;
    window.print();
    document.body.innerHTML = originalContents;
    window.location.reload();
  };

  if (!transaction) return null;

  const tx = transaction;
  const dateStr = new Date(tx.created_at).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });
  const timeStr = new Date(tx.created_at).toLocaleTimeString('fr-FR', {
    hour: '2-digit', minute: '2-digit'
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-sm w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        {/* Aperçu du reçu */}
        <div ref={receiptRef}>
          <div style={{
            width: '80mm',
            maxWidth: '100%',
            margin: '0 auto',
            padding: '8mm 5mm',
            fontFamily: "'Courier New', Courier, monospace",
            fontSize: '12px',
            lineHeight: '1.4',
            color: '#000',
            background: '#fff',
          }}>
            {/* En-tête entreprise */}
            <div style={{ textAlign: 'center', marginBottom: '10px', borderBottom: '1px dashed #000', paddingBottom: '10px' }}>
              {settings?.logo && (
                <img
                  src={settings.logo.startsWith('http') ? settings.logo : `${BACKEND_URL}${settings.logo}`}
                  alt="Logo"
                  style={{ width: '50px', height: '50px', objectFit: 'contain', margin: '0 auto 5px', display: 'block' }}
                />
              )}
              <div style={{ fontWeight: 'bold', fontSize: '14px', textTransform: 'uppercase' }}>
                {settings?.nom_entreprise || 'Mon Entreprise'}
              </div>
              {settings?.adresse && <div style={{ fontSize: '10px' }}>{settings.adresse}</div>}
              {settings?.telephone && <div style={{ fontSize: '10px' }}>Tél: {settings.telephone}</div>}
              {settings?.numero_registre && <div style={{ fontSize: '10px' }}>RCCM: {settings.numero_registre}</div>}
            </div>

            {/* Titre opération */}
            <div style={{ textAlign: 'center', marginBottom: '10px', fontWeight: 'bold', fontSize: '14px' }}>
              REÇU {TYPE_LABELS[tx.type] || tx.type.toUpperCase()}
            </div>

            {/* Infos transaction */}
            <div style={{ marginBottom: '10px', borderBottom: '1px dashed #000', paddingBottom: '10px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '2px 0', fontSize: '11px' }}>Date</td>
                    <td style={{ padding: '2px 0', textAlign: 'right', fontSize: '11px' }}>{dateStr} {timeStr}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '2px 0', fontSize: '11px' }}>Opérateur</td>
                    <td style={{ padding: '2px 0', textAlign: 'right', fontSize: '11px', fontWeight: 'bold' }}>{tx.operator_nom}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '2px 0', fontSize: '11px' }}>N° Client</td>
                    <td style={{ padding: '2px 0', textAlign: 'right', fontSize: '11px' }}>{tx.numero_client}</td>
                  </tr>
                  {tx.numero_destinataire && (
                    <tr>
                      <td style={{ padding: '2px 0', fontSize: '11px' }}>N° Destinataire</td>
                      <td style={{ padding: '2px 0', textAlign: 'right', fontSize: '11px' }}>{tx.numero_destinataire}</td>
                    </tr>
                  )}
                  {tx.reference && (
                    <tr>
                      <td style={{ padding: '2px 0', fontSize: '11px' }}>Référence</td>
                      <td style={{ padding: '2px 0', textAlign: 'right', fontSize: '11px' }}>{tx.reference}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Montants */}
            <div style={{ marginBottom: '10px', borderBottom: '1px dashed #000', paddingBottom: '10px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  <tr>
                    <td style={{ padding: '3px 0', fontSize: '13px', fontWeight: 'bold' }}>Montant</td>
                    <td style={{ padding: '3px 0', textAlign: 'right', fontSize: '13px', fontWeight: 'bold' }}>{formatMoney(tx.montant)}</td>
                  </tr>
                  {parseFloat(tx.frais_client) > 0 && (
                    <tr>
                      <td style={{ padding: '2px 0', fontSize: '11px' }}>Frais client</td>
                      <td style={{ padding: '2px 0', textAlign: 'right', fontSize: '11px' }}>{formatMoney(tx.frais_client)}</td>
                    </tr>
                  )}
                  {parseFloat(tx.frais_client) > 0 && (
                    <tr>
                      <td style={{ padding: '3px 0', fontSize: '12px', fontWeight: 'bold', borderTop: '1px solid #000' }}>Total payé</td>
                      <td style={{ padding: '3px 0', textAlign: 'right', fontSize: '12px', fontWeight: 'bold', borderTop: '1px solid #000' }}>
                        {formatMoney(parseFloat(tx.montant) + parseFloat(tx.frais_client))}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Notes */}
            {tx.notes && (
              <div style={{ marginBottom: '10px', fontSize: '10px', fontStyle: 'italic' }}>
                Note: {tx.notes}
              </div>
            )}

            {/* Pied de page */}
            <div style={{ textAlign: 'center', fontSize: '10px', marginTop: '10px', borderTop: '1px dashed #000', paddingTop: '10px' }}>
              <div>Merci pour votre confiance !</div>
              <div style={{ marginTop: '3px' }}>Transaction #{tx.id}</div>
              {settings?.telephone && <div>Service client: {settings.telephone}</div>}
            </div>

            {/* Style impression format ticket */}
            <style>{`
              @media print {
                @page { 
                  size: 80mm auto; 
                  margin: 0; 
                }
                html, body {
                  width: 80mm;
                  margin: 0;
                  padding: 0;
                  background: #fff;
                  -webkit-print-color-adjust: exact;
                }
                nav, header, .navbar, .sidebar, footer,
                .print\\:hidden, button { 
                  display: none !important; 
                }
              }
            `}</style>
          </div>
        </div>

        {/* Boutons d'action (hors zone d'impression) */}
        <div className="flex gap-3 p-4 border-t bg-gray-50 print:hidden">
          <button onClick={handlePrint}
            className="flex-1 bg-indigo-600 text-white px-4 py-2.5 rounded-lg hover:bg-indigo-700 font-medium flex items-center justify-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Imprimer
          </button>
          <button onClick={onClose}
            className="px-4 py-2.5 rounded-lg border text-gray-600 hover:bg-gray-100 font-medium">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileMoneyReceipt;
